import { EXERCISE_LIBRARY, MUSCLE_GROUP_LIST, type CuratedExercise, type MuscleGroupKey } from "@/lib/exerciseLibrary";
import { getExplicitFitnessMovement } from "@/lib/fitnessExerciseIdentity";

const GROUPS_STORAGE_KEY = "vyv_free_workout_groups";
const SELECTIONS_STORAGE_KEY = "vyv_free_workout_selections";
export type FreeWorkoutSelections = Partial<Record<MuscleGroupKey, string[]>>;

/** Navigation and browser storage are untrusted inputs, even for a local setup. */
export function normalizeFreeWorkoutGroups(value: unknown): MuscleGroupKey[] {
  if (!Array.isArray(value)) return [];
  return MUSCLE_GROUP_LIST.filter(group => value.includes(group));
}

function movementKey(exercise: CuratedExercise): string {
  const movement = getExplicitFitnessMovement(exercise.i18nKey);
  return movement ? "movement:" + movement.id : "exercise:" + exercise.id;
}

/** Collapse only explicitly approved same-movement identities, preserving source order. */
export function getFreeWorkoutExerciseChoices(group: MuscleGroupKey): CuratedExercise[] {
  const seen = new Set<string>();
  return EXERCISE_LIBRARY[group].filter(exercise => {
    const key = movementKey(exercise);
    if (seen.has(key)) return false;
    seen.add(key); return true;
  });
}

/** Old source IDs/keys select the one visible representative; unknown/hidden picks disappear. */
export function normalizeFreeWorkoutSelections(value: unknown, groups: readonly MuscleGroupKey[]): FreeWorkoutSelections {
  const source = value && typeof value === "object" && !Array.isArray(value) ? value : null;
  const result: FreeWorkoutSelections = {};
  for (const group of groups) {
    const raw: unknown = source && Object.prototype.hasOwnProperty.call(source, group) ? (source as Record<string, unknown>)[group] : null;
    const requested = new Set(Array.isArray(raw) ? raw.filter(item => typeof item === "string") : []);
    const selected = new Set(EXERCISE_LIBRARY[group].filter(exercise => requested.has(exercise.id) || requested.has(exercise.i18nKey)).map(movementKey));
    result[group] = getFreeWorkoutExerciseChoices(group).filter(exercise => selected.has(movementKey(exercise))).map(exercise => exercise.id);
  }
  return result;
}

export function readFreeWorkoutGroups(): MuscleGroupKey[] {
  try {
    const raw = sessionStorage.getItem(GROUPS_STORAGE_KEY);
    return raw ? normalizeFreeWorkoutGroups(JSON.parse(raw)) : [];
  } catch {
    // Setup remains usable when browser storage is unavailable or malformed.
    return [];
  }
}

export function readFreeWorkoutSelections(groups: readonly MuscleGroupKey[]): FreeWorkoutSelections {
  try {
    const raw = sessionStorage.getItem(SELECTIONS_STORAGE_KEY);
    return normalizeFreeWorkoutSelections(raw ? JSON.parse(raw) : null, groups);
  } catch {
    return normalizeFreeWorkoutSelections(null, groups);
  }
}

export function saveFreeWorkoutSelections(value: FreeWorkoutSelections, groups: readonly MuscleGroupKey[]): void {
  try { sessionStorage.setItem(SELECTIONS_STORAGE_KEY, JSON.stringify(normalizeFreeWorkoutSelections(value, groups))); }
  catch { /* Selection remains usable without browser storage. */ }
}

export function clearFreeWorkoutSetup(): void {
  try { sessionStorage.removeItem(GROUPS_STORAGE_KEY); sessionStorage.removeItem(SELECTIONS_STORAGE_KEY); }
  catch { /* Navigation still works without browser storage. */ }
}
